import { useState } from "react";
import { createPortal } from "react-dom";
import { Button, Tooltip } from "@tricura/atlas-ui";
import { FeaturedAlert } from "./featured-alert";

/**
 * Submission section footer — Andressa's "separate buttons" flow (jun-23).
 *
 *   • Save     — primary; persists the section WITHOUT navigating. Disabled
 *                when there are no unsaved changes (tooltip "No changes to
 *                be saved."). The debounced auto-save (Loss Runs / Premium)
 *                keeps it disabled most of the time — both flows coexist.
 *   • Continue — secondary; navigates to the next section. Tooltip
 *                "Continue to {next section}". If clicked while there are
 *                unsaved changes, it does NOT navigate and surfaces the
 *                "Unsaved Changes" alert instead (save-first guard).
 */
interface SubmissionFooterProps {
    /** Persist the section without navigating. Enabled only when dirty. */
    onSave?: () => void;
    /** Navigate to the next section. Blocked (with an alert) while dirty. */
    onContinue?: () => void;
    /** Whether the section has unsaved changes — drives the Save button's
     *  enabled state and the Continue save-first guard. */
    isDirty?: boolean;
    isSaving?: boolean;
    /** Label of the next section, for the Continue tooltip
     *  ("Continue to Schedule"). Null/undefined on the last section. */
    nextSectionLabel?: string | null;
    /** When set, Save is disabled and this text is its tooltip — for
     *  section-level validation that must block the save (e.g. a draft row
     *  that would duplicate existing data). Null/empty = no block. */
    saveBlockedReason?: string | null;
}

export function SubmissionFooter({ onSave, onContinue, isDirty = false, isSaving = false, nextSectionLabel, saveBlockedReason }: SubmissionFooterProps) {
    // Local guard: Continue while dirty surfaces this alert instead of navigating.
    const [showUnsaved, setShowUnsaved] = useState(false);

    const handleContinue = () => {
        if (isDirty) {
            setShowUnsaved(true);
            return;
        }
        onContinue?.();
    };

    const saveDisabled = !isDirty || isSaving || !!saveBlockedReason;
    const saveTooltip = isSaving
        ? "Saving…"
        : saveBlockedReason
          ? saveBlockedReason
          : isDirty
            ? "Save your changes before continuing"
            : "No changes to be saved.";
    const continueTooltip = nextSectionLabel ? `Continue to ${nextSectionLabel}` : "Finish submission";

    return (
        <>
            {/* Footer cancels its parent section's `px-4 sm:px-8` so the top
              * border stretches edge-to-edge, then re-applies the padding. */}
            <footer className="border-secondary bg-primary sticky bottom-0 z-10 mt-auto -mx-4 flex items-center justify-end gap-3 border-t px-4 py-4 sm:-mx-8 sm:px-8">
                {/* Save is omitted on read-only sections (no onSave). The span
                  * wrapper lets the tooltip trigger while the button is
                  * disabled ("No changes to be saved."). */}
                {onSave && (
                    <Tooltip title={saveTooltip}>
                        <span className="inline-flex">
                            <Button
                                color="primary"
                                onClick={onSave}
                                isDisabled={saveDisabled}
                                isLoading={isSaving}
                                showTextWhileLoading
                                data-autotest="submission-footer-save-button"
                            >
                                Save
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Tooltip title={continueTooltip}>
                    <Button color="secondary" onClick={handleContinue} data-autotest="submission-footer-continue-button">
                        Continue
                    </Button>
                </Tooltip>
            </footer>

            {showUnsaved &&
                createPortal(
                    <div className="fixed inset-x-0 top-4 z-[100] flex justify-center px-4">
                        <div className="w-full max-w-md" data-autotest="submission-footer-unsaved-alert">
                            <FeaturedAlert
                                color="warning"
                                title="Unsaved Changes"
                                description="You have unsaved changes on this page. Save before continuing to avoid losing information."
                                onClose={() => setShowUnsaved(false)}
                            />
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    );
}
